import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { activityStore } from '../../lib/activity'
import { mineral } from '../../theme'

/**
 * La "veta": una línea bajo la barra superior que corre mientras el sistema
 * trabaja. Lecturas = veta fina del color del módulo; escrituras = veta gruesa;
 * cuando una escritura termina, la línea se completa en verde (guardado) o en
 * ají (falló). Es el eco visual de la acción que el usuario acaba de hacer.
 */
export default function ActivityBar({ accent }: { accent: string }) {
  const s = useSyncExternalStore(activityStore.subscribe, activityStore.getSnapshot)
  const busy = s.reads + s.writes > 0
  const [flash, setFlash] = useState<null | 'success' | 'error'>(null)
  const lastSeq = useRef<number | null>(null)

  useEffect(() => {
    if (!s.last || s.last.seq === lastSeq.current) return
    lastSeq.current = s.last.seq
    // eslint-disable-next-line react-hooks/set-state-in-effect -- dispara el destello del desenlace
    setFlash(s.last.outcome)
    const t = setTimeout(() => setFlash(null), 900)
    return () => clearTimeout(t)
  }, [s.last])

  const flashColor = flash === 'error' ? mineral.aji : mineral.bosque500

  return (
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 3, overflow: 'hidden', pointerEvents: 'none' }} aria-hidden>
      <AnimatePresence>
        {busy && !flash && (
          <motion.div
            key="busy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'absolute', inset: 0, height: s.writes > 0 ? 3 : 2 }}
          >
            <motion.div
              animate={{ x: ['-40%', '140%'] }}
              transition={{ duration: s.writes > 0 ? 0.9 : 1.3, repeat: Infinity, ease: [0.45, 0, 0.55, 1] }}
              style={{ width: '45%', height: '100%', borderRadius: 3, background: `linear-gradient(90deg, transparent, ${accent}, ${s.writes > 0 ? mineral.oro : accent}, transparent)` }}
            />
          </motion.div>
        )}
        {flash && (
          <motion.div
            key={`flash-${flash}`}
            initial={{ scaleX: 0, opacity: 1 }}
            animate={{ scaleX: 1, opacity: [1, 1, 0] }}
            transition={{ duration: 0.85, ease: [0.2, 0.7, 0.2, 1], times: [0, 0.6, 1] }}
            style={{ position: 'absolute', inset: 0, transformOrigin: 'left', background: flashColor, boxShadow: `0 0 12px ${flashColor}` }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
